import { IAcknowledgeServicingAuditIssueParams } from '@common/interfaces/servicingAudit'
import { IUser } from '@common/interfaces/user'
import Base from './base'

export default class ClientInfo extends Base {
  list(params: object) {
    return this.apiClient.get('clients', params)
  }

  show(id: string) {
    return this.apiClient.get(`clients/${id}`)
  }

  update(id: string, data: object) {
    return this.apiClient.post(`clients/${id}`, data)
  }

  updateTerms(id: string, data: object) {
    return this.apiClient.put(`clients/${id}/terms`, data)
  }

  calculateTerminate(id: string) {
    return this.apiClient.get(`clients/${id}/terminate`)
  }

  generateTerminationLetter(id: string) {
    return this.apiClient.get(`clients/${id}/termination-letter`)
  }

  terminate(id: string, data: object) {
    return this.apiClient.put(`clients/${id}/terminate`, data)
  }

  addManager({ clientIds, managerId }: { clientIds: string[]; managerId: string }) {
    return this.apiClient.post(`clients/managers/${managerId}`, { clientIds })
  }

  addOrRemoveManager({ clientIds, managerId }: { clientIds: string[]; managerId: string }) {
    return this.apiClient.put(`clients/managers/${managerId}`, { clientIds })
  }

  removeManager({ clientIds, managerId }: { clientIds: string[]; managerId: string }) {
    return this.apiClient.delete(`clients/managers/${managerId}`, {
      clientIds,
    })
  }

  notifyManager({ clientId, managerId }: { clientId: string; managerId: string }) {
    return this.apiClient.post(`clients/${clientId}/managers/${managerId}/notify`)
  }

  updateManager({
    clientId,
    managerId,
    data,
  }: {
    clientId: string
    managerId: string
    data: Partial<IUser>
  }) {
    return this.apiClient.put(`clients/${clientId}/managers/${managerId}`, data)
  }

  clientInfo() {
    return this.apiClient.get('clients/me')
  }

  listAccountActivity(
    clientId?: string,
    params?: {
      page?: number
      perPage?: number
      filters?: object
      orderBy?: string
      orderDirection?: string
      onlyFundings?: boolean
      activityId?: string
    },
  ) {
    return this.apiClient.get(`clients/${clientId || 'me'}/activity`, params)
  }

  getAccountActivityDateRange(clientId?: string) {
    return this.apiClient.get(`clients/${clientId || 'me'}/activity/date-range`)
  }

  getComplianceStatusLegal(clientId: string, params?: object) {
    return this.apiClient.get(`clients/${clientId}/compliance-status/legal`, params)
  }

  getComplianceStatusFinancials(clientId: string, params?: object) {
    return this.apiClient.get(`clients/${clientId}/compliance-status/financials`, params)
  }

  getComplianceEntityDetails(clientId: string, params?: { documentType: string }) {
    return this.apiClient.get(`clients/${clientId}/compliance-entity-details`, params)
  }

  exportAccountActivity(clientId?: string, params?: object) {
    return this.apiClient.get(`clients/${clientId || 'me'}/activity`, {
      ...params,
      isFile: true,
      isExport: true,
    })
  }

  deleteAccountActivity(clientId: string, data: object) {
    return this.apiClient.delete(`clients/${clientId}/activity`, data)
  }

  getRequestFunding() {
    return this.apiClient.get('clients/me/request-funding')
  }

  requestFunding(data: FormData) {
    return this.apiClient.post('clients/me/request-funding', data)
  }

  modifyFunding(data: FormData) {
    return this.apiClient.put('clients/me/request-funding', data)
  }

  cancelFunding(data: object) {
    return this.apiClient.delete('clients/me/request-funding', data)
  }

  getBanner() {
    return this.apiClient.get('clients/me/banner')
  }

  closeBanner(id: string) {
    return this.apiClient.post(`clients/me/banner/${id}`)
  }

  submitOngoingReporting(id: string, data: FormData) {
    return this.apiClient.post(`clients/${id}/ongoing-reporting`, data)
  }

  createRequiredReports(data: object) {
    return this.apiClient.post('clients/me/required-reports', data)
  }

  listClientsAggregation(params: object) {
    return this.apiClient.get('clients/aggregation', params)
  }

  listOveradvances(id: string, params?: object) {
    return this.apiClient.get(`clients/${id}/overadvances`, params)
  }

  createOveradvance(id: string, data: object) {
    return this.apiClient.post(`clients/${id}/overadvances`, data)
  }

  updateOveradvance(clientId: string, overadvanceId: string, data: object) {
    return this.apiClient.put(`clients/${clientId}/overadvances/${overadvanceId}`, data)
  }

  reorderOveradvances(clientId: string, priorityOrder: string[]) {
    return this.apiClient.put(`clients/${clientId}/overadvances-reorder`, { priorityOrder })
  }

  deleteOveradvance(clientId: string, overadvanceId: string) {
    return this.apiClient.delete(`clients/${clientId}/overadvances/${overadvanceId}`)
  }

  createOveradvanceActivity(clientId: string, overadvanceId: string, data: object) {
    return this.apiClient.post(`clients/${clientId}/overadvances/${overadvanceId}/activities`, data)
  }

  listOveradvanceActivities(clientId: string, overadvanceId: string) {
    return this.apiClient.get(`clients/${clientId}/overadvances/${overadvanceId}/activities`)
  }

  deleteOveradvanceActivity(clientId: string, overadvanceId: string, activityId: string) {
    return this.apiClient.delete(
      `clients/${clientId}/overadvances/${overadvanceId}/activities/${activityId}`,
    )
  }

  listCompliance(params: object) {
    return this.apiClient.get('clients/compliance', params)
  }

  listComplianceSummaryMetrics() {
    return this.apiClient.get('clients/compliance/summary-metrics')
  }

  listComplianceAudit() {
    return this.apiClient.get('clients/compliance/audit')
  }

  acknowledgeComplianceAuditIssue(data: IAcknowledgeServicingAuditIssueParams) {
    return this.apiClient.post('clients/compliance/audit/acknowledge', data)
  }

  updateUnderwriting(id: string, data: object) {
    return this.apiClient.put(`clients/${id}/underwriting`, data)
  } //TODO: Implement soon

  createFunding(id: string, data: object) {
    return this.apiClient.post(`clients/${id}/funding`, data)
  }

  updateFunding(id: string, data: object) {
    return this.apiClient.put(`clients/${id}/funding`, data)
  }

  updatePassthroughs(id: string, data: object) {
    return this.apiClient.put(`clients/${id}/passthroughs`, data)
  }

  deletePassthroughs(id: string, data: object) {
    return this.apiClient.delete(`clients/${id}/passthroughs`, data)
  }

  listTermLoans(id: string) {
    return this.apiClient.get(`clients/${id}/term-loans`)
  }

  listClientOveradvances(id: string) {
    return this.apiClient.get(`clients/${id}/overadvances-balances`)
  }

  listAmortizationSchedule(id: string, termLoanId: string) {
    return this.apiClient.get(`clients/${id}/term-loans/${termLoanId}/amortization-schedule`)
  }

  createTermLoan(id: string, data: object) {
    return this.apiClient.post(`clients/${id}/term-loans`, data)
  }

  updateTermLoan(id: string, termLoanId: string, data: object) {
    return this.apiClient.put(`clients/${id}/term-loans/${termLoanId}`, data)
  }

  deleteTermLoan(id: string, termLoanId: string) {
    return this.apiClient.delete(`clients/${id}/term-loans/${termLoanId}`)
  }

  createTermLoanActivity(id: string, termLoanId: string, data: object) {
    return this.apiClient.post(`clients/${id}/term-loans/${termLoanId}/activity`, data)
  }

  updateTermLoanActivity(id: string, termLoanId: string, activityId: string, data: object) {
    return this.apiClient.put(`clients/${id}/term-loans/${termLoanId}/activity/${activityId}`, data)
  }

  deleteTermLoanActivity(id: string, termLoanId: string, activityId: string) {
    return this.apiClient.delete(`clients/${id}/term-loans/${termLoanId}/activity/${activityId}`)
  }

  listArEligibility(id: string, data: object) {
    return this.apiClient.get(`clients/${id}/ar-ineligibility`, data)
  }

  listOtherReserves(id: string, params: object = {}) {
    return this.apiClient.get(`clients/${id}/reserves`, params)
  }

  createOtherReserve(id: string, data: object) {
    return this.apiClient.post(`/clients/${id}/reserves`, data)
  }

  updateOtherReserve(id: string, itemId: string, data: object) {
    return this.apiClient.patch(`/clients/${id}/reserves/${itemId}`, data)
  }

  deleteOtherReserve(id: string, itemId: string) {
    return this.apiClient.delete(`/clients/${id}/reserves/${itemId}`)
  }

  listInventoryCategories(id: string, data: object) {
    return this.apiClient.get(`clients/${id}/inventory-categories/list`, data)
  }

  listMasterInventory(id: string, data: object) {
    return this.apiClient.get(`clients/${id}/master-inventory/list`, data)
  }

  listEligibleFields(id: string) {
    return this.apiClient.get(`clients/${id}/inventory-eligible-fields`)
  }

  listInventoryLocations(id: string, params?: { withoutWaiver?: boolean; eligibleOnly?: boolean }) {
    return this.apiClient.get(`clients/${id}/inventory-locations`, params)
  }

  updateEligibleFieldsAliases(id: string, data: object) {
    return this.apiClient.put(`clients/${id}/inventory-ineligibility-fields`, data)
  }

  listCustomRules(data: {
    id?: string
    borrowingBaseId?: string
    orderBy?: string
    orderDirection?: string
    filters?: object
  }) {
    const { id, orderBy, orderDirection, filters } = data
    return this.apiClient.get(`clients/${id}/inventory-eligible-custom-rules`, {
      orderBy,
      orderDirection,
      filters,
    })
  }

  listCustomRulesLabels(id: string) {
    return this.apiClient.get(`clients/${id}/inventory-eligible-custom-rules/labels`)
  }

  listCustomRulesValues(id: string, data: object) {
    return this.apiClient.get(`clients/${id}/inventory-eligible-custom-rules/values`, data)
  }

  listAmendments(id: string, data: object) {
    return this.apiClient.get(`clients/${id}/amendments`, data)
  }

  createAmendment(id: string, data: object) {
    return this.apiClient.post(`clients/${id}/amendments`, data)
  }

  updateAmendment(id: string, itemId: string, values: object) {
    return this.apiClient.put(`clients/${id}/amendments/${itemId}`, values)
  }

  deleteAmendment(id: string, itemId: string) {
    return this.apiClient.delete(`clients/${id}/amendments/${itemId}`)
  }

  exportPortfolioReview() {
    return this.apiClient.post('clients/exports/portfolio-review/', {}, { isFile: true })
  }
  exportParticipantPortfolioReview() {
    return this.apiClient.post(
      'clients/exports/participant-portfolio-review/',
      {},
      { isFile: true },
    )
  }

  listApEligibility(id: string, data: object) {
    return this.apiClient.get(`clients/${id}/ap-ineligibility`, data)
  }

  updateApEligibility(id: string, itemId: string, data: object) {
    return this.apiClient.put(`clients/${id}/ap-ineligibility/${itemId}`, data)
  }

  listFinancialsReports(id: string) {
    return this.apiClient.get(`clients/${id}/financials-reports`)
  }

  listFinancialsSummary(id: string, data: object) {
    return this.apiClient.get(`clients/${id}/financials-summary`, data)
  }
  listRiskRating(id: string) {
    return this.apiClient.get(`clients/${id}/risk-rating`)
  }

  listRiskRatingDetails(data: object) {
    return this.apiClient.get(`clients/risk-rating-details`, data)
  }

  // Get all risk rating details without pagination for chart view
  listRiskRatingDetailsAll(data: object) {
    return this.apiClient.get(`clients/risk-rating-details`, { ...data })
  }

  listRiskRatingDetailsSummaryMetrics() {
    return this.apiClient.get(`clients/risk-rating-details-summary-metrics`)
  }

  // Enqueues a whole-portfolio recompute; returns as soon as it is queued, not when it lands.
  refreshRiskRating() {
    return this.apiClient.post(`clients/risk-rating-refresh`)
  }

  listFinancialsComparison(data: object) {
    return this.apiClient.get(`clients/financials-comparison`, data)
  }

  listBBCs(id: string, params: { from?: string; to?: string } = {}) {
    return this.apiClient.get(`clients/${id}/bbcs`, params)
  }

  listInventoryHealth(id: string) {
    return this.apiClient.get(`clients/${id}/inventory-health`)
  }

  showCashFlowInfo(id: string, data: object) {
    return this.apiClient.get(`clients/${id}/cash-flow`, data)
  }

  exportMasterInventory(id: string, data: object) {
    return this.apiClient.post(`clients/${id}/master-inventory/export`, data, {
      isFile: true,
    })
  }
  bulkUpdateMasterInventory(id: string, data: object) {
    return this.apiClient.put(`clients/${id}/master-inventory/bulk-update`, data)
  }

  exportArEligibility(id: string, data: object) {
    return this.apiClient.post(`clients/${id}/ar-eligibility/export`, data, {
      isFile: true,
    })
  }

  bulkUpdateArEligibility(id: string, data: object) {
    return this.apiClient.put(`clients/${id}/ar-eligibility/bulk-update`, data)
  }
  listCustomers(id?: string) {
    return this.apiClient.get(`clients/${id || 'me'}/list-customers`)
  }
  refreshAutoReporting(data: object = {}) {
    return this.apiClient.post('clients/refresh-auto-reporting', data)
  }
  getAccountActivityGraph(id: string, data: object) {
    return this.apiClient.get(`clients/${id}/account-activity-graph`, data)
  }

  listLoanLedger(id: string, data: object) {
    return this.apiClient.get(`clients/${id}/loan-ledger`, data)
  }

  listStressCaseAssumptions(id: string, data: object) {
    return this.apiClient.get(`clients/${id}/stress-case`, data)
  }

  updateStressCaseAssumption(id: string, assumptionId: string, data: object) {
    return this.apiClient.put(`clients/${id}/stress-case/${assumptionId}`, data)
  }

  listCollectionsReconciliation(id: string, params: object) {
    return this.apiClient.get(`clients/${id}/collections-reconciliation`, params)
  }

  listCollectionsReconciliationOptions(id: string, params: object) {
    return this.apiClient.get(`clients/${id}/collections-reconciliation-options`, params)
  }

  listCustomerCollections(id: string, params: object) {
    return this.apiClient.get(`clients/${id}/customer-collections`, params)
  }

  listSalesChannelDistribution(id: string, params: object) {
    return this.apiClient.get(`clients/${id}/sales-channel-distribution`, params)
  }

  listSalesChannelDistributionOptions(id: string, params: object) {
    return this.apiClient.get(`clients/${id}/sales-channel-distribution-options`, params)
  }

  listArLedgerActivity(id: string, params: object) {
    return this.apiClient.get(`clients/${id}/ar-ledger-activity`, params)
  }

  listCustomerInvoicingVsCollections(id: string, params: object) {
    return this.apiClient.get(`clients/${id}/customer-invoicing-vs-collections`, params)
  }

  listPendingCashDominion(id: string, data: object) {
    return this.apiClient.get(`clients/${id}/pending-cash-dominion`, data)
  }

  waivePendingCashDominion(
    id: string,
    params: { entityId: string; waived: boolean; waiverReason?: string },
  ) {
    return this.apiClient.post(`clients/${id}/pending-cash-dominion/waive`, params)
  }

  listAllPendingCashDominion(data: object) {
    return this.apiClient.get(`clients/all-pending-cash-dominion`, {
      ...data,
      isAllClients: true,
    })
  }

  listCheckAliases(id: string) {
    return this.apiClient.get(`clients/${id}/check-aliases`)
  }

  updateCheckAliases(id: string, data: object) {
    return this.apiClient.put(`clients/${id}/check-aliases`, data)
  }

  getTermLoansAndOveradvances(params?: object) {
    return this.apiClient.get('clients/term-loans-and-overadvances', params)
  }

  exportTermLoansAndOveradvances(params?: object) {
    return this.apiClient.post('clients/exports/term-loans-and-overadvances/', params, {
      isFile: true,
    })
  }

  getMasterInvoice(invoiceId: string) {
    return this.apiClient.get(`master-invoices/${invoiceId}`)
  }

  listMasterInvoices(id: string, data: object) {
    return this.apiClient.get(`clients/${id}/master-invoices/list`, data)
  }

  getMasterInvoiceBbcHistory(invoiceId: string, data?: object) {
    return this.apiClient.get(`master-invoices/${invoiceId}/bbc-history`, data)
  }

  getMasterInvoicePaymentActivity(invoiceId: string, clientName: string) {
    return this.apiClient.get(`master-invoices/${invoiceId}/payment-activity`, { clientName })
  }

  updateMasterInvoice(id: string, data: object) {
    return this.apiClient.put(`clients/${id}/master-invoices`, data)
  }
}
